import { useCallback } from 'react';
import { type QuickActionPageTarget } from 'src/components/QuickActions/quick-action-pages.types';
import { useGlobalStore } from 'src/store';
import useNavigationStore from 'src/store/navigation';
import { useSidekickTransition } from 'src/utils/navigation/use-sidekick-transition';
import { trimDuplicatedSlashes, useNavigateRoute } from 'src/utils/routing/use-navigate-route';
import { useScreenDimensions } from 'src/utils/system';

const toComparablePath = (path: string) => trimDuplicatedSlashes(`/${path}/`);

export const useOpenQuickActionPage = () => {
  const { navigate, pathnameWithoutPrefix } = useNavigateRoute();
  const setActivePane = useNavigationStore((state) => state.setActivePane);
  const setPendingPane = useNavigationStore((state) => state.setPendingPane);
  const setNavbar = useGlobalStore.use.setNavbar();
  const { isMobile } = useScreenDimensions();
  const { dismissMovedCue } = useSidekickTransition();

  return useCallback(
    (target: QuickActionPageTarget) => {
      if (target.kind === 'pane') {
        if (target.pane === 'sidekick') {
          dismissMovedCue();
        }
        setNavbar({ isOpen: true });
        setActivePane(target.pane);
        return;
      }

      if (isMobile) {
        setNavbar({ isOpen: false });
      }

      // the route→pane sync only runs on a path change, so a pending pane for the
      // current path would linger and land on whatever the user opens next
      if (toComparablePath(target.path) === toComparablePath(pathnameWithoutPrefix)) {
        setActivePane(target.navPane);
        return;
      }

      // Home routes don't move the nav off Inbox or Sidekick on their own
      setPendingPane(target.navPane);
      navigate(target.path);
    },
    [
      dismissMovedCue,
      isMobile,
      navigate,
      pathnameWithoutPrefix,
      setActivePane,
      setNavbar,
      setPendingPane,
    ],
  );
};
